import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { ItemType, ItemTypeMap } from '@/common/api/types/itemType';
import { ItemTypeIcon } from '@/common/components/ui/item-type/itemTypeIcon';
import { OverflowTooltip } from '@/common/components/ui/OverflowTooltip';
import { useRegisterExtraItemIds, useVisibleItems } from '../context/VisibleItemsContext';
import { CellTextSkeleton } from './withSkeleton';

type ResolvedItem = { id: number; name: string; itemType?: ItemType };

export const MultiItemRefCell = ({
  value,
  itemTypesById,
}: {
  value?: number[];
  itemTypesById?: ItemTypeMap;
}) => {
  const { t } = useTranslation();
  useRegisterExtraItemIds(value);
  const { itemsById, restrictedIds } = useVisibleItems();

  const restrictedLabel = t(($) => $.views.table.items.restrictedItemName);

  const { isAnyLoading, resolved } = React.useMemo(() => {
    if (!value?.length) return { isAnyLoading: false, resolved: [] as ResolvedItem[] };
    let loading = false;
    const list: ResolvedItem[] = [];
    for (const id of value) {
      const item = itemsById.get(id);
      if (item) {
        const itemType =
          item.itemTypeId !== undefined && item.itemTypeId !== null
            ? itemTypesById?.[item.itemTypeId]
            : undefined;
        list.push({ id, name: item.name ?? '', itemType });
      } else if (restrictedIds.has(id)) {
        list.push({ id, name: restrictedLabel });
      } else {
        loading = true;
      }
    }
    return { isAnyLoading: loading, resolved: list };
  }, [value, itemsById, itemTypesById, restrictedIds, restrictedLabel]);

  if (!value?.length) return null;
  if (isAnyLoading) return <CellTextSkeleton />;
  if (!resolved.length) return null;

  return (
    <OverflowTooltip
      tooltipContent={
        <span className="whitespace-pre-wrap">{resolved.map((r) => r.name).join('\n')}</span>
      }
    >
      {resolved.map((r, i) => (
        <span key={r.id} className="inline-flex items-center gap-1">
          {r.itemType && <ItemTypeIcon itemType={r.itemType} aria-hidden />}
          {r.name}
          {i < resolved.length - 1 ? ', ' : null}
        </span>
      ))}
    </OverflowTooltip>
  );
};
